import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload, CheckCircle2, AlertTriangle, ChevronLeft, ChevronRight, FileText, Trash2, LockKeyhole, Loader2, Paperclip, X, Mail, Home as HomeIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['Upload', 'Boetegegevens', 'Persoonsgegevens', 'Volmacht', 'Bevestiging'];

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Math.round((d - new Date()) / (1000 * 60 * 60 * 24));
};

const formatDateNL = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Normaliseer Nederlandse tekst: alleen eerste letter van elk woord hoofdletter,
// initialen (J., A.B.) blijven hoofdletter, tussenvoegsels (van, de, den) blijven kleine letter,
// postcode blijft hoofdletter.
const TUSSENVOEGSELS = new Set(['van', 'de', 'der', 'den', 'ter', 'ten', 'te', 'op', 'in', 'aan', 'bij', 'het']);
const properCase = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.trim().split(/\s+/).map((w, idx) => {
    if (!w) return w;
    // initial like "J." or "A.B."
    if (/^([A-Za-z]\.){1,}$/.test(w)) return w.toUpperCase();
    const lower = w.toLowerCase();
    if (idx > 0 && TUSSENVOEGSELS.has(lower)) return lower;
    // Handle words with dashes (Van-Bergen) or slashes
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }).join(' ');
};

const normalizePostcode = (pc) => {
  if (!pc) return pc;
  const cleaned = pc.replace(/\s+/g, '').toUpperCase();
  if (/^\d{4}[A-Z]{2}$/.test(cleaned)) return cleaned.slice(0, 4) + ' ' + cleaned.slice(4);
  return pc.toUpperCase();
};

const SignaturePad = ({ onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const start = (e) => {
    drawing.current = true;
    const c = ref.current; const ctx = c.getContext('2d');
    ctx.beginPath();
    const p = pos(e, c); ctx.moveTo(p.x, p.y);
  };
  const pos = (e, c) => {
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const move = (e) => {
    if (!drawing.current) return;
    const c = ref.current; const ctx = c.getContext('2d');
    const p = pos(e, c);
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#1e1b4b';
    ctx.lineTo(p.x, p.y); ctx.stroke();
    if (e.touches) e.preventDefault();
  };
  const end = () => {
    if (drawing.current && ref.current) {
      const data = ref.current.toDataURL();
      onChange?.(data);
    }
    drawing.current = false;
  };
  const clear = () => {
    const c = ref.current; c.getContext('2d').clearRect(0, 0, c.width, c.height);
    onChange?.(null);
  };
  return (
    <div>
      <canvas
        ref={ref} width={600} height={160}
        className="sig-canvas w-full bg-white border border-slate-300 rounded-md"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <button type="button" onClick={clear} className="text-sm text-slate-500 hover:text-slate-800 mt-2">Veld leegmaken</button>
    </div>
  );
};

const FieldGrid = ({ children }) => (
  <div className="grid md:grid-cols-2 gap-4">{children}</div>
);

const Field = ({ label, value, onChange, placeholder, type = 'text', required, invalid }) => (
  <div>
    <Label className="text-xs text-slate-600">{label} {required && <span className="text-rose-500">*</span>}</Label>
    <Input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`mt-1 ${invalid ? 'border-rose-400 focus-visible:ring-rose-300' : ''}`}
    />
  </div>
);

const Indienen = () => {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [boete, setBoete] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  // Additional docs (aanvullende fotos/documenten)
  const [additionalFiles, setAdditionalFiles] = useState([]); // [{file_id, file_name, uploading?}]
  const [uploadingAdditional, setUploadingAdditional] = useState(false);

  // Authorization file for bedrijf/lease
  const [authFile, setAuthFile] = useState(null); // {file_id, file_name}
  const [uploadingAuth, setUploadingAuth] = useState(false);

  const [form, setForm] = useState({
    aanhef: 'Dhr.',
    naam: '',
    email: '',
    telefoon: '',
    straat: '',
    postcode: '',
    woonplaats: '',
    naam_op_boete: 'Op mijn naam',
    bedrijf: '',
    lease: '',
    reden: '',
    machtiging: false,
    voorwaarden: false,
    handtekening: null,
  });
  const [ticket, setTicket] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const handleFile = async (f) => {
    setFile(f);
    setAnalyzing(true);
    setBoete(null);
    setFileId(null);
    setFileName(f.name);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/ocr/extract`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      const result = res.data.fields || {};
      // Normalize NAW casing
      const normalized = {
        ...result,
        naam_geadresseerde: properCase(result.naam_geadresseerde),
        straat_geadresseerde: properCase(result.straat_geadresseerde),
        woonplaats_geadresseerde: properCase(result.woonplaats_geadresseerde),
        postcode_geadresseerde: normalizePostcode(result.postcode_geadresseerde),
        locatie_gedraging: result.locatie_gedraging ? properCase(result.locatie_gedraging) : result.locatie_gedraging,
      };
      setFileId(res.data.file_id);
      setBoete(normalized);
      setForm(prev => ({
        ...prev,
        naam: prev.naam || (normalized.naam_geadresseerde || '').replace(/^(De heer|Mevr\.|Dhr\.)\s*/i, '').trim(),
        straat: prev.straat || normalized.straat_geadresseerde || '',
        postcode: prev.postcode || normalized.postcode_geadresseerde || '',
        woonplaats: prev.woonplaats || normalized.woonplaats_geadresseerde || '',
        aanhef: /mevr/i.test(normalized.naam_geadresseerde || '') ? 'Mevr.' : 'Dhr.',
      }));
      toast({ title: 'Document verwerkt', description: 'Controleer de gegevens op de volgende stap.' });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Onbekende fout';
      toast({ title: 'Document kon niet worden verwerkt', description: msg, variant: 'destructive' });
      setFile(null);
    } finally {
      setAnalyzing(false);
    }
  };

  const uploadPlain = async (f) => {
    const fd = new FormData();
    fd.append('file', f);
    const res = await axios.post(`${API}/files`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return res.data; // {file_id, file_name}
  };

  const onAdditionalFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingAdditional(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const res = await uploadPlain(f);
        uploaded.push({ file_id: res.file_id, file_name: res.file_name });
      }
      setAdditionalFiles(prev => [...prev, ...uploaded]);
      toast({ title: `${uploaded.length} bestand(en) toegevoegd` });
    } catch (e) {
      toast({ title: 'Uploaden mislukt', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setUploadingAdditional(false);
    }
  };

  const removeAdditional = (idx) => {
    setAdditionalFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const onAuthFile = async (f) => {
    if (!f) return;
    setUploadingAuth(true);
    try {
      const res = await uploadPlain(f);
      setAuthFile({ file_id: res.file_id, file_name: res.file_name });
      toast({ title: 'Machtiging/contract geüpload' });
    } catch (e) {
      toast({ title: 'Uploaden mislukt', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setUploadingAuth(false);
    }
  };

  const requiresAuthDoc = form.naam_op_boete !== 'Op mijn naam';
  const requiresBedrijfNaam = form.naam_op_boete === 'Op naam van een bedrijf';
  const requiresLeaseNaam = form.naam_op_boete === 'Op naam van de leasemaatschappij';

  // Validation per step
  const stepErrors = () => {
    const errs = {};
    if (step === 1 && boete) {
      // Step 1 = Boetegegevens - NAW required
      if (!boete.naam_geadresseerde) errs.naam_geadresseerde = true;
      if (!boete.straat_geadresseerde) errs.straat_geadresseerde = true;
      if (!boete.postcode_geadresseerde) errs.postcode_geadresseerde = true;
      if (!boete.woonplaats_geadresseerde) errs.woonplaats_geadresseerde = true;
      if (!boete.beschikkingsnummer) errs.beschikkingsnummer = true;
      if (!boete.kenteken) errs.kenteken = true;
    }
    if (step === 2) {
      if (!form.naam) errs.naam = true;
      if (!form.email) errs.email = true;
      if (!form.telefoon) errs.telefoon = true;
      if (!form.straat) errs.straat = true;
      if (!form.postcode) errs.postcode = true;
      if (!form.woonplaats) errs.woonplaats = true;
      if (requiresBedrijfNaam && !form.bedrijf) errs.bedrijf = true;
      if (requiresLeaseNaam && !form.lease) errs.lease = true;
      if (requiresAuthDoc && !authFile) errs.authFile = true;
      if (!form.reden || form.reden.trim().length < 5) errs.reden = true;
    }
    return errs;
  };

  const errors = stepErrors();

  const canGoNext = () => {
    if (step === 0) return !!file && !!boete;
    if (step === 1) return Object.keys(stepErrors()).length === 0;
    if (step === 2) return Object.keys(stepErrors()).length === 0;
    if (step === 3) return form.machtiging && form.voorwaarden && form.handtekening;
    return true;
  };

  const next = () => {
    if (!canGoNext()) {
      setShowErrors(true);
      toast({ title: 'Verplichte velden ontbreken', description: 'Vul alle rood gemarkeerde velden in.', variant: 'destructive' });
      return;
    }
    setShowErrors(false);
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const prev = () => { setShowErrors(false); setStep(s => Math.max(s - 1, 0)); };

  const submit = async () => {
    if (!canGoNext()) { setShowErrors(true); return; }
    try {
      const payload = {
        boete: boete || {},
        client: form,
        file_id: fileId,
        file_name: fileName,
        authorization_file_id: authFile?.file_id || null,
        authorization_file_name: authFile?.file_name || null,
        additional_file_ids: additionalFiles.map(f => f.file_id),
        additional_file_names: additionalFiles.map(f => f.file_name),
      };
      const res = await axios.post(`${API}/submissions`, payload, { timeout: 30000 });
      setTicket({
        id: res.data.kenmerk,
        beschikkingsnummer: boete?.beschikkingsnummer,
        datum: new Date(res.data.created_at).toLocaleDateString('nl-NL'),
        type: boete?.type,
        instantie: boete?.instantie,
        bedrag: boete?.bedrag,
        deadline: formatDateNL(boete?.uiterlijke_datum_bezwaar),
      });
      setStep(STEPS.length - 1);
      toast({ title: 'Bezwaar ontvangen', description: `Kenmerk ${res.data.kenmerk}` });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast({ title: 'Aanmelding mislukt', description: msg, variant: 'destructive' });
    }
  };

  const bezwaarDagen = boete ? daysUntil(boete.uiterlijke_datum_bezwaar) : null;
  const showErr = (key) => showErrors && errors[key];

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="hero-gradient text-white py-12">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="text-3xl md:text-4xl font-bold uppercase">Vul het formulier hieronder in</h1>
          <p className="text-slate-200 mt-2">Gratis bezwaar maken in 5 simpele stappen.</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 md:px-6 -mt-6 pb-16">
        <Card className="p-6 md:p-8 border border-slate-200 bg-white">
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
              <span>Stap {step + 1} van {STEPS.length}: <strong className="text-[#1e1b4b]">{STEPS[step]}</strong></span>
              <span>{Math.round(((step + 1) / STEPS.length) * 100)}%</span>
            </div>
            <Progress value={((step + 1) / STEPS.length) * 100} className="h-2" />
          </div>

          {/* Step 0 - Upload */}
          {step === 0 && (
            <div>
              <h2 className="text-xl font-bold text-[#1e1b4b]">Upload jouw boete</h2>
              <p className="text-sm text-slate-600 mt-1">Voeg een duidelijke foto of PDF van je beschikking toe. Wij lezen de gegevens automatisch uit.</p>
              <div
                onDrop={onDrop}
                onDragOver={e => e.preventDefault()}
                className="mt-5 border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-[#f26b1f] transition-colors bg-slate-50"
              >
                {!file && (
                  <>
                    <Upload className="w-10 h-10 text-slate-400 mx-auto" />
                    <div className="mt-3 font-semibold text-[#1e1b4b]">Kies of sleep een bestand</div>
                    <div className="text-xs text-slate-500 mt-1">Ondersteund: JPG, PNG, PDF (max 10MB)</div>
                    <label className="mt-4 inline-block">
                      <input type="file" className="hidden" accept="image/*,application/pdf" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
                      <span className="cursor-pointer inline-flex items-center gap-2 bg-[#1e1b4b] hover:bg-[#2a2464] text-white px-5 py-2.5 rounded-md text-sm font-medium">
                        <Upload className="w-4 h-4" /> Bestand kiezen
                      </span>
                    </label>
                  </>
                )}
                {file && analyzing && (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-8 h-8 text-[#f26b1f] animate-spin" />
                    <div className="mt-3 font-semibold text-[#1e1b4b]">Document verwerken...</div>
                    <div className="text-xs text-slate-500 mt-1">Dit duurt hooguit een paar seconden</div>
                  </div>
                )}
                {file && !analyzing && boete && (
                  <div className="flex flex-col items-center">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                    <div className="mt-2 font-semibold text-[#1e1b4b]">Document ontvangen: {file.name}</div>
                    <button className="text-xs text-slate-500 mt-2 hover:text-slate-800 inline-flex items-center gap-1" onClick={() => { setFile(null); setBoete(null); }}>
                      <Trash2 className="w-3 h-3" /> Ander bestand
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs text-slate-500">
                <LockKeyhole className="w-4 h-4 text-slate-400 mt-0.5" />
                <span>Je document wordt end-to-end versleuteld opgeslagen conform AVG.</span>
              </div>
            </div>
          )}

          {/* Step 1 - Boetegegevens */}
          {step === 1 && boete && (
            <div>
              <h2 className="text-xl font-bold text-[#1e1b4b]">Gegevens van de beschikking</h2>
              <p className="text-sm text-slate-600 mt-1 mb-5">Controleer de gegevens die wij van je boete hebben overgenomen en pas ze aan waar nodig.</p>

              {bezwaarDagen !== null && (
                <div className={`p-4 rounded-lg mb-5 flex items-start gap-3 border ${
                  bezwaarDagen < 0 ? 'bg-rose-50 border-rose-200' :
                  bezwaarDagen < 7 ? 'bg-rose-50 border-rose-200' :
                  bezwaarDagen < 14 ? 'bg-amber-50 border-amber-200' :
                  'bg-emerald-50 border-emerald-200'
                }`}>
                  {bezwaarDagen < 14 ? <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />}
                  <div className="text-sm">
                    <div className="font-semibold text-[#1e1b4b]">Uiterlijke datum bezwaar/beroep: {formatDateNL(boete.uiterlijke_datum_bezwaar)}</div>
                    <div className="text-slate-700 mt-0.5">
                      {bezwaarDagen < 0
                        ? `De termijn is ${Math.abs(bezwaarDagen)} dagen geleden verstreken. Neem direct contact met ons op via e-mail om te bespreken of er nog mogelijkheden zijn.`
                        : `Nog ${bezwaarDagen} ${bezwaarDagen === 1 ? 'dag' : 'dagen'} om bezwaar te maken. ${bezwaarDagen < 14 ? 'We raden aan snel te handelen.' : ''}`}
                    </div>
                  </div>
                </div>
              )}

              <Badge className="bg-[#1e1b4b]/10 text-[#1e1b4b] border-0 mb-4 hover:bg-[#1e1b4b]/15">Type: {boete.type || 'onbekend'}</Badge>

              <div className="space-y-6">
                <div>
                  <div className="text-sm font-semibold text-[#1e1b4b] mb-3 uppercase tracking-wide">NAW-gegevens (zoals op beschikking)</div>
                  <FieldGrid>
                    <Field label="Naam geadresseerde" value={boete.naam_geadresseerde} onChange={v => setBoete({ ...boete, naam_geadresseerde: v })} required invalid={showErr('naam_geadresseerde')} />
                    <Field label="Straat en huisnummer" value={boete.straat_geadresseerde} onChange={v => setBoete({ ...boete, straat_geadresseerde: v })} required invalid={showErr('straat_geadresseerde')} />
                    <Field label="Postcode" value={boete.postcode_geadresseerde} onChange={v => setBoete({ ...boete, postcode_geadresseerde: v })} required invalid={showErr('postcode_geadresseerde')} />
                    <Field label="Woonplaats" value={boete.woonplaats_geadresseerde} onChange={v => setBoete({ ...boete, woonplaats_geadresseerde: v })} required invalid={showErr('woonplaats_geadresseerde')} />
                  </FieldGrid>
                </div>

                <div>
                  <div className="text-sm font-semibold text-[#1e1b4b] mb-3 uppercase tracking-wide">Beschikking</div>
                  <FieldGrid>
                    <Field label="Beschikkingsnummer" value={boete.beschikkingsnummer} onChange={v => setBoete({ ...boete, beschikkingsnummer: v })} required invalid={showErr('beschikkingsnummer')} />
                    <Field label="Datum beschikking" type="date" value={boete.datum_beschikking} onChange={v => setBoete({ ...boete, datum_beschikking: v })} />
                    <Field label="Instantie" value={boete.instantie} onChange={v => setBoete({ ...boete, instantie: v })} />
                    <Field label="Bedrag (€)" type="number" value={boete.bedrag} onChange={v => setBoete({ ...boete, bedrag: parseFloat(v) || 0 })} />
                    <Field label="Uiterlijke datum bezwaar/beroep" type="date" value={boete.uiterlijke_datum_bezwaar} onChange={v => setBoete({ ...boete, uiterlijke_datum_bezwaar: v })} />
                    <Field label="Kenteken" value={boete.kenteken} onChange={v => setBoete({ ...boete, kenteken: v.toUpperCase() })} placeholder="12-ABC-3" required invalid={showErr('kenteken')} />
                  </FieldGrid>
                </div>

                <div>
                  <div className="text-sm font-semibold text-[#1e1b4b] mb-3 uppercase tracking-wide">Vermeende gedraging</div>
                  <div className="grid gap-4">
                    <div>
                      <Label className="text-xs text-slate-600">Omschrijving overtreding</Label>
                      <Textarea rows={2} value={boete.omschrijving_overtreding || ''} onChange={e => setBoete({ ...boete, omschrijving_overtreding: e.target.value })} className="mt-1" />
                    </div>
                    <FieldGrid>
                      <Field label="Tijdstip vermeende gedraging" type="datetime-local" value={boete.tijdstip_gedraging?.replace(' ', 'T')} onChange={v => setBoete({ ...boete, tijdstip_gedraging: v.replace('T', ' ') })} />
                      <Field label="Locatie gedraging" value={boete.locatie_gedraging} onChange={v => setBoete({ ...boete, locatie_gedraging: v })} />
                    </FieldGrid>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2 - Persoonsgegevens */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-bold text-[#1e1b4b] mb-1">Persoonsgegevens</h2>
              <p className="text-sm text-slate-600 mb-5">Wij hebben je gegevens nodig om het bezwaar correct in te dienen.</p>
              <div className="space-y-4">
                <div>
                  <Label>Aanhef</Label>
                  <RadioGroup value={form.aanhef} onValueChange={v => setForm({ ...form, aanhef: v })} className="flex gap-4 mt-2">
                    {['Dhr.', 'Mevr.'].map(v => (
                      <div key={v} className="flex items-center gap-2">
                        <RadioGroupItem value={v} id={v} />
                        <Label htmlFor={v} className="cursor-pointer">{v}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
                <FieldGrid>
                  <Field label="Voorletter(s) en achternaam" value={form.naam} onChange={v => setForm({ ...form, naam: v })} required invalid={showErr('naam')} />
                  <Field label="E-mail" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} required invalid={showErr('email')} />
                  <Field label="Telefoonnummer" value={form.telefoon} onChange={v => setForm({ ...form, telefoon: v })} required invalid={showErr('telefoon')} />
                  <Field label="Straat en huisnummer" value={form.straat} onChange={v => setForm({ ...form, straat: v })} required invalid={showErr('straat')} />
                  <Field label="Postcode" value={form.postcode} onChange={v => setForm({ ...form, postcode: v })} placeholder="1234 AB" required invalid={showErr('postcode')} />
                  <Field label="Woonplaats" value={form.woonplaats} onChange={v => setForm({ ...form, woonplaats: v })} required invalid={showErr('woonplaats')} />
                </FieldGrid>

                <div>
                  <Label>Op welke naam staat de boete?</Label>
                  <RadioGroup value={form.naam_op_boete} onValueChange={v => setForm({ ...form, naam_op_boete: v })} className="flex flex-col gap-2 mt-2">
                    {['Op mijn naam', 'Op naam van een bedrijf', 'Op naam van de leasemaatschappij'].map(v => (
                      <div key={v} className="flex items-center gap-2">
                        <RadioGroupItem value={v} id={v} />
                        <Label htmlFor={v} className="cursor-pointer">{v}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {requiresBedrijfNaam && (
                  <Field label="Naam van het bedrijf" value={form.bedrijf} onChange={v => setForm({ ...form, bedrijf: v })} required invalid={showErr('bedrijf')} />
                )}
                {requiresLeaseNaam && (
                  <Field label="Naam van de leasemaatschappij" value={form.lease} onChange={v => setForm({ ...form, lease: v })} required invalid={showErr('lease')} />
                )}

                {/* Authorization file upload for bedrijf/lease */}
                {requiresAuthDoc && (
                  <div>
                    <Label>Voeg hier machtiging of contract toe <span className="text-rose-500">*</span></Label>
                    <p className="text-xs text-slate-500 mt-1">Aangezien de boete niet op jouw persoonlijke naam staat, hebben wij een aanvullende machtiging of contract van het bedrijf/de leasemaatschappij nodig.</p>
                    <div className={`mt-2 border-2 border-dashed rounded-lg p-4 ${showErr('authFile') ? 'border-rose-400 bg-rose-50' : 'border-slate-300 bg-slate-50'}`}>
                      {!authFile && !uploadingAuth && (
                        <label className="cursor-pointer flex items-center gap-2 text-sm text-[#1e1b4b] font-medium">
                          <Paperclip className="w-4 h-4" />
                          <span>Kies een bestand (PDF/JPG/PNG)</span>
                          <input type="file" className="hidden" accept="image/*,application/pdf" onChange={e => e.target.files?.[0] && onAuthFile(e.target.files[0])} />
                        </label>
                      )}
                      {uploadingAuth && (
                        <div className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="w-4 h-4 animate-spin" /> Uploaden...</div>
                      )}
                      {authFile && !uploadingAuth && (
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> {authFile.file_name}</div>
                          <button onClick={() => setAuthFile(null)} className="text-slate-500 hover:text-rose-600"><X className="w-4 h-4" /></button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Additional files */}
                <div>
                  <Label>Aanvullende documenten of foto's <span className="text-slate-400 font-normal">(optioneel)</span></Label>
                  <p className="text-xs text-slate-500 mt-1">Bijvoorbeeld dashcam-beelden, foto's van de situatie, of een parkeerbewijs. Meerdere bestanden mogelijk.</p>
                  <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50">
                    <label className="cursor-pointer flex items-center gap-2 text-sm text-[#1e1b4b] font-medium">
                      <Paperclip className="w-4 h-4" />
                      <span>Bestand(en) toevoegen</span>
                      <input type="file" className="hidden" multiple accept="image/*,application/pdf" onChange={e => onAdditionalFiles(e.target.files)} />
                    </label>
                    {uploadingAdditional && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-slate-600"><Loader2 className="w-4 h-4 animate-spin" /> Uploaden...</div>
                    )}
                    {additionalFiles.length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {additionalFiles.map((f, i) => (
                          <li key={f.file_id} className="flex items-center justify-between text-sm bg-white rounded px-3 py-2 border border-slate-200">
                            <div className="flex items-center gap-2 text-slate-700"><FileText className="w-4 h-4 text-slate-400" /> {f.file_name}</div>
                            <button onClick={() => removeAdditional(i)} className="text-slate-500 hover:text-rose-600"><X className="w-4 h-4" /></button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div>
                  <Label>Leg hier uit waarom je het oneens bent met de boete <span className="text-rose-500">*</span></Label>
                  <Textarea rows={4} value={form.reden} onChange={e => setForm({ ...form, reden: e.target.value })} className={`mt-1 ${showErr('reden') ? 'border-rose-400 focus-visible:ring-rose-300' : ''}`} placeholder="Beschrijf zo duidelijk mogelijk waarom je bezwaar wilt maken..." />
                  {showErr('reden') && <div className="text-xs text-rose-600 mt-1">Vul minimaal een korte uitleg in.</div>}
                </div>
              </div>
            </div>
          )}

          {/* Step 3 - Machtiging */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-bold text-[#1e1b4b] mb-1">Volmacht &amp; samenvatting</h2>
              <p className="text-sm text-slate-600 mb-4">Om jou kosteloos te kunnen vertegenwoordigen, machtig je ons om namens jou op te treden.</p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 text-sm text-slate-700 space-y-2">
                <div><strong className="text-[#1e1b4b]">Winnen we?</strong> Jij krijgt het bedrag van de boete volledig terug.</div>
                <div><strong className="text-[#1e1b4b]">Verliezen we?</strong> Dan betaal je niets. Je betaalt in dat geval enkel de boete.</div>
                <div>De overheid vergoedt onze dienstverlening apart bij winst. Deze vergoeding draag je aan ons over.</div>
              </div>
              <div className="mt-5 space-y-3">
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <Checkbox checked={form.machtiging} onCheckedChange={v => setForm({ ...form, machtiging: !!v })} className="mt-0.5" />
                  <span>Ik heb het <Link to="/machtigingsformulier" target="_blank" rel="noopener noreferrer" className="text-[#f26b1f] underline">machtigingsformulier</Link> gelezen, begrijp het en ga ermee akkoord.</span>
                </label>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <Checkbox checked={form.voorwaarden} onCheckedChange={v => setForm({ ...form, voorwaarden: !!v })} className="mt-0.5" />
                  <span>Ik heb de <Link to="/algemene-voorwaarden" target="_blank" rel="noopener noreferrer" className="text-[#f26b1f] underline">algemene voorwaarden</Link> gelezen, begrijp ze en ga ermee akkoord.</span>
                </label>
              </div>
              <div className="mt-6">
                <Label>Handtekening <span className="text-rose-500">*</span></Label>
                <div className="mt-2">
                  <SignaturePad onChange={sig => setForm({ ...form, handtekening: sig })} />
                </div>
              </div>
            </div>
          )}

          {/* Step 4 - Bevestiging */}
          {step === 4 && ticket && (
            <div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-9 h-9 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-[#1e1b4b] mt-4">Bezwaar ontvangen!</h2>
                <p className="text-slate-600 mt-2">We gaan voor je aan de slag.</p>

                <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-lg mx-auto">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left">
                    <div className="text-xs text-slate-500">Ons kenmerk</div>
                    <div className="font-mono font-bold text-[#1e1b4b] text-lg">{ticket.id}</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left">
                    <div className="text-xs text-slate-500">Beschikkingsnummer</div>
                    <div className="font-mono font-bold text-[#1e1b4b] text-lg break-all">{ticket.beschikkingsnummer || '-'}</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-[#1e1b4b]">Wat gebeurt er nu?</h3>
                  <ul className="mt-3 space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2"><span className="w-5 h-5 rounded-full bg-[#f26b1f]/10 text-[#f26b1f] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span><span>Onze juristen bekijken de beschikking zorgvuldig en beoordelen de kans van slagen.</span></li>
                    <li className="flex items-start gap-2"><span className="w-5 h-5 rounded-full bg-[#f26b1f]/10 text-[#f26b1f] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span><span>Zodra wij een eerste beoordeling hebben gedaan, komen we bij je terug met meer informatie.</span></li>
                    <li className="flex items-start gap-2"><span className="w-5 h-5 rounded-full bg-[#f26b1f]/10 text-[#f26b1f] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span><span>Daarna houden we je op de hoogte van de verdere voortgang van je bezwaar.</span></li>
                  </ul>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-800">
                    <div className="font-bold text-amber-900">Belangrijk om te weten</div>
                    <p className="mt-1">Het indienen van bezwaar schort de betalingsverplichting <strong>niet</strong> op. Dit betekent dat je de boete of aanslag wel gewoon op tijd moet betalen, ook al loopt de bezwaarprocedure.</p>
                    <p className="mt-2">Mocht je bezwaar gegrond worden verklaard, dan wordt het bedrag door de instantie aan jou terugbetaald.</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-[#1e1b4b]">Vragen?</h3>
                  <p className="text-sm text-slate-700 mt-2">Heb je in de tussentijd vragen of wil je extra informatie? Mail ons via <a href="mailto:info@bezwaarmeesters.nl" className="text-[#f26b1f] underline inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> info@bezwaarmeesters.nl</a>.</p>
                  <p className="text-sm text-slate-700 mt-3 italic">Samen zorgen we ervoor dat je geen onterechte kosten hoeft te dragen.</p>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <Button className="bg-[#1e1b4b] hover:bg-[#2a2464] text-white h-11 px-6" onClick={() => navigate('/')}>
                  <HomeIcon className="w-4 h-4 mr-2" /> Terug naar home
                </Button>
              </div>
            </div>
          )}

          {/* Navigation */}
          {step < 4 && (
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200">
              <Button variant="outline" onClick={prev} disabled={step === 0}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Vorige
              </Button>
              {step < 3 ? (
                <Button className="bg-[#f26b1f] hover:bg-[#d4581a] text-white" onClick={next}>
                  Volgende <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button className="bg-[#f26b1f] hover:bg-[#d4581a] text-white" onClick={submit} disabled={!canGoNext()}>
                  Bezwaar indienen <FileText className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          )}
        </Card>
      </section>
    </div>
  );
};

export default Indienen;
